import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import {
  faBellPlus,
  faSpinnerThird,
  faTools
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/shared/Button';
import CustomSelect from 'components/shared/CustomSelect';
import FormGroup from 'components/shared/FormGroup';
import {
  StyledFormGroupLabel,
  StyledFormGroupLabelWrapper
} from 'components/shared/FormGroup.styled';
import { HelpTooltip } from 'components/shared/HelpTooltip/HelpTooltip';
import HtmlTrans from 'components/shared/HtmlTrans';
import InputPlacesAutocomplete from 'components/shared/InputPlacesAutocomplete';
import InputText from 'components/shared/InputText';
import { LinkText } from 'components/shared/LinkText';
import Switcher from 'components/shared/Switcher';
import { ListItemStringValue } from 'interfaces/ui/ui-list-item';
import isEqual from 'lodash/isEqual';
import { Location } from 'model';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { config } from 'utils/config';
import { DEFAULT_FILTER } from '../JobOffersListComponent.constants';
import { DEFAULT_CURRENCY_VALUE, REMOTE_OPTIONS } from './Filters.constants';
import { useOfferListFilters } from './Filters.hooks';
import {
  StyledFilters,
  StyledFiltersAction,
  StyledFiltersBg,
  StyledFiltersContainer,
  StyledFiltersTitle,
  StyledSalaryDoubleField,
  StyledSalaryFieldset
} from './Filters.styled';
import { ListFiltersProps } from './Filters.types';

const Filters: React.FC<ListFiltersProps> = ({
  currentFilter,
  isSavingOfferAlert,
  currencies,
  languages,
  techs,
  techRoles,
  allLanguages,
  onFilterChange,
  onFilterReset,
  onManageAlertsClick,
  onCreateAlertClick,
  onClose,
  className
}) => {
  const {
    t,
    currencyValues,
    languageValues,
    getLabelValueForCurrentFilterLanguages,
    handleResetFiltersClick,
    updateFilter
  } = useOfferListFilters({
    currencies: currencies,
    currentFilter: currentFilter,
    languages: languages,
    onFilterChange: onFilterChange,
    onFilterReset: onFilterReset
  });

  const { locale } = useRouter();

  // Alerts can only be created if some "saveable" filters are applied
  // (meaning that they are different from the default filters)
  // currency and onlyActive are not considered "saveable" filters.
  const userCanCreateAlerts = useMemo(
    () =>
      !isEqual(
        {
          locations: currentFilter.locations || DEFAULT_FILTER.locations,
          minLanguages:
            currentFilter.minLanguages || DEFAULT_FILTER.minLanguages,
          minSalary: currentFilter.minSalary || DEFAULT_FILTER.minSalary,
          offerLanguage:
            currentFilter.offerLanguage || DEFAULT_FILTER.offerLanguage,
          remoteOptions:
            currentFilter.remoteOptions || DEFAULT_FILTER.remoteOptions,
          techRoles: currentFilter.techRoles || DEFAULT_FILTER.techRoles,
          techs: currentFilter.techs || DEFAULT_FILTER.techs,
          text: currentFilter.text || DEFAULT_FILTER.text,
          currency: undefined,
          onlyActive: undefined
        },
        {
          ...DEFAULT_FILTER,
          currency: undefined,
          onlyActive: undefined
        }
      ),
    [
      currentFilter.locations,
      currentFilter.minLanguages,
      currentFilter.minSalary,
      currentFilter.offerLanguage,
      currentFilter.remoteOptions,
      currentFilter.techRoles,
      currentFilter.techs,
      currentFilter.text
    ]
  );

  return (
    <div className={className}>
      <StyledFiltersBg onClick={onClose} />
      <StyledFilters>
        <StyledFiltersTitle>
          {t('filters_title')}

          <Button
            sizes="sm"
            variant="tertiary"
            isIcon
            onClick={onClose}
            className="close-filters"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </StyledFiltersTitle>

        <StyledFiltersContainer>
          <Switcher
            id="activeOffers"
            label={t('only_active')}
            onChange={(e) =>
              updateFilter({
                ...currentFilter,
                onlyActive: e.target.checked
              })
            }
            checked={currentFilter.onlyActive}
            className="filters-item"
          />

          <FormGroup
            label={t('techRoles')}
            helpContent={
              <HtmlTrans
                ns="job-offer-list"
                i18nKey="filters_tooltips.roles"
                components={{
                  rolesLink: (
                    <LinkText href={getAppUrl(APP_URLS.roles, locale)} />
                  )
                }}
              />
            }
            forId="techRoles"
            className="filters-item"
          >
            <CustomSelect
              id="techRoles"
              isSmall
              value={currentFilter.techRoles?.map((techRoleId) =>
                techRoles.find((el) => el.value === techRoleId)
              )}
              options={techRoles}
              onChange={(selectedOptions: ListItemStringValue[]) => {
                updateFilter({
                  ...currentFilter,
                  techRoles: selectedOptions.map((opt) => +opt.value)
                });
              }}
              isMulti
              hideDropdownUntilSearch
              placeholder={t('techRoles_placeholder')}
            />
          </FormGroup>

          <FormGroup
            label={t('techs')}
            helpContent={
              <HtmlTrans
                ns="job-offer-list"
                i18nKey="filters_tooltips.techs"
                components={{
                  supportLink: (
                    <a
                      href={`mailto:${config.supportEmail}`}
                      target="_blank"
                      rel="noreferrer"
                    />
                  )
                }}
                values={{
                  email: config.supportEmail
                }}
              />
            }
            forId="techs"
            className="filters-item"
          >
            <CustomSelect
              id="techs"
              isSmall
              value={currentFilter.techs?.map((techId) =>
                techs.find((el) => el.value === techId)
              )}
              options={techs}
              matchFrom="start"
              onChange={(selectedOptions: ListItemStringValue[]) => {
                updateFilter({
                  ...currentFilter,
                  techs: selectedOptions.map((opt) => +opt.value)
                });
              }}
              isMulti
              hideDropdownUntilSearch
              placeholder={t('techs_placeholder')}
              multiValueSeparator="or"
            />
          </FormGroup>

          <FormGroup
            label={t('text')}
            helpContent={t('filters_tooltips.text')}
            forId="offerSearch"
            className="filters-item"
          >
            <InputText
              id="offerSearch"
              type="text"
              placeholder={t('text_placeholder')}
              title={t('text_title')}
              sizes="sm"
              value={currentFilter.text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateFilter({
                  ...currentFilter,
                  text: e.target.value
                })
              }
            />
          </FormGroup>

          <StyledSalaryFieldset className="filters-item">
            <StyledFormGroupLabelWrapper>
              <StyledFormGroupLabel as="legend">
                {t('min_salary')}{' '}
              </StyledFormGroupLabel>

              <HelpTooltip message={t('filters_tooltips.minSalary')} />
            </StyledFormGroupLabelWrapper>
            <StyledSalaryDoubleField>
              <FormGroup
                label="Select currency"
                forId="salaryCurrency"
                isLabelHidden
              >
                <CustomSelect
                  id="salaryCurrency"
                  placeholder="€"
                  options={currencyValues}
                  isSmall
                  value={
                    currentFilter.currency
                      ? {
                          label: currentFilter.currency,
                          value: currentFilter.currency
                        }
                      : DEFAULT_CURRENCY_VALUE
                  }
                  onChange={(selection) =>
                    updateFilter({
                      ...currentFilter,
                      currency: selection.value
                    })
                  }
                />
              </FormGroup>

              <FormGroup
                label={t('min_salary')}
                forId="minSalary"
                isLabelHidden
              >
                <InputText
                  id="minSalary"
                  type="number"
                  step="any"
                  min={0}
                  placeholder={t('min_salary_placeholder')}
                  title={t('min_salary_title')}
                  variant="thousands"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilter({
                      ...currentFilter,
                      minSalary: e.target.value
                    })
                  }
                  sizes="sm"
                  value={currentFilter.minSalary}
                  onKeyDown={(e) => {
                    if (isNaN(+e.key) && e.key !== 'Backspace')
                      e.preventDefault();
                  }}
                />
              </FormGroup>
            </StyledSalaryDoubleField>
          </StyledSalaryFieldset>

          <FormGroup
            label={t('remote_options')}
            helpContent={t('filters_tooltips.remotePercentage')}
            forId="remoteOtions"
            className="filters-item"
          >
            <CustomSelect
              id="remoteOtions"
              isSmall
              value={REMOTE_OPTIONS.filter((opt) =>
                currentFilter.remoteOptions?.includes(opt.value)
              )}
              options={REMOTE_OPTIONS}
              formatOptionLabel={(opt) => t(`remote_options_items.${opt}`)}
              onChange={(selectedOptions: ListItemStringValue[]) => {
                updateFilter({
                  ...currentFilter,
                  remoteOptions: selectedOptions.map((opt) => opt.value)
                });
              }}
              isMulti
            />
          </FormGroup>

          <FormGroup
            label={t('location')}
            helpContent={t('filters_tooltips.location')}
            forId="location"
            className="filters-item"
          >
            <InputPlacesAutocomplete
              isSmall
              value={currentFilter.locations}
              autocompleteTypes={['locality']}
              onChange={(place) => {
                const locations = place as Location[];

                updateFilter({
                  ...currentFilter,
                  locations
                });
              }}
              isMulti
            />
          </FormGroup>

          <FormGroup
            label={t('language')}
            helpContent={t('filters_tooltips.languages')}
            forId="language"
            className="filters-item"
          >
            <CustomSelect
              id="language"
              placeholder={t('any_language')}
              defaultValue={languageValues[0]}
              options={languageValues}
              value={getLabelValueForCurrentFilterLanguages()}
              isSmall
              onChange={(selection) =>
                updateFilter({
                  ...currentFilter,
                  offerLanguage: selection.value
                })
              }
            />
          </FormGroup>
        </StyledFiltersContainer>

        <StyledFiltersAction>
          <Button
            sizes="xs"
            variant="primary"
            isLoading={isSavingOfferAlert}
            className="alerts"
            onClick={onCreateAlertClick}
            disabled={!userCanCreateAlerts}
          >
            {!isSavingOfferAlert && <FontAwesomeIcon icon={faBellPlus} />}
            {isSavingOfferAlert && (
              <FontAwesomeIcon icon={faSpinnerThird} spin />
            )}
            <span>{t('create_alert')}</span>
          </Button>

          <Button
            sizes="xs"
            variant="secondary"
            className="alerts"
            onClick={onManageAlertsClick}
          >
            <FontAwesomeIcon icon={faTools} />
            <span>{t('open_alerts')}</span>
          </Button>

          <Button
            sizes="xs"
            variant="secondary"
            onClick={handleResetFiltersClick}
            disabled={!userCanCreateAlerts}
          >
            <FontAwesomeIcon icon={faCircleXmark} />
            <span>{t('reset_filter')}</span>
          </Button>
        </StyledFiltersAction>
      </StyledFilters>
    </div>
  );
};

export default Filters;
